import { View, StyleSheet, Pressable } from "react-native";
import { useEffect, useState } from "react";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { PlusCircle, Trash, EditIcon } from "../../svgIcons";
import CustomText from "../../common/general/CustomText/CustomText";
import { ScrollView } from "react-native-gesture-handler";
import { ListItem } from "@rneui/themed";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { actions, useAppState } from "../../../contexts/AppStateContext";
import ContentMenuHeader from "./ContentMenuHeader";
import ContentMenuItem from "./ContentMenuItem";
import CustomModal from "../../common/general/CustomModal/CustomModal";
import DeleteModule from "../../content/DeleteModule/DeleteModule";
import AddModuleModalContainer from "../../content/ContentModalContainers/AddModuleModalContainer";
import EditAddAssignmentModalContainer from "../../content/ContentModalContainers/EditAddAssignmentModalContainer";
import EditModuleNameModalContainer from "../../content/ContentModalContainers/EditModuleNameModalContainer";
import { useNavigation } from "@react-navigation/native";

function ContentMenu({
  modulesRoute,
  data,
  loadingData,
  courseName,
  onItemPress,
  onRenameModule,
  onDeleteModule,
  onAddModule,
  onAddAssignment,
  editModuleNameModalState,
  editAssignmentModalState,
  addModuleModalState,
  deleteModuleModalState,
  activelyEditingState,
}) {
  const { isXLarge, isMedium, hp } = useResponsiveScreen();

  const [selectedItem, setSelectedItem] = useState();
  const [selectedModule, setSelectedModule] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);

  const [deleteModuleModalVisible, setDeleteModuleModalVisible] = deleteModuleModalState;
  const [addModuleModalVisible, setAddModuleModalVisible] = addModuleModalState;
  const [editAssignmentModalVisible, setEditAssignmentModalVisible] = editAssignmentModalState;
  const [editModuleNameModalVisible, setEditModuleNameModalVisible] = editModuleNameModalState;

  const selectedModuleData = data?.modules?.find((module) => module.module_name === selectedModule);
  const navigation = useNavigation();

  const { dispatch } = useAppState();

  useEffect(() => {
    if (data?.modules?.length > 0) {
      const firstModule = data.modules?.[0];
      setSelectedModule(firstModule.module_name);

      if (data?.modules.assignments?.length > 0) {
        const firstAssignment = data?.modules.assignments[0];
        setSelectedItem(firstAssignment.assignment_name);

        navigation.navigate(modulesRoute, {
          courseId: data?.id,
          courseName: data.name,
          moduleIndex: data?.modules?.indexOf(firstModule),
        });
      }
    }
  }, [data]);

  const handleAddModule = () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setAddModuleModalVisible(true);
  };
  const handleEditModuleName = (moduleName) => {
    setSelectedModule(moduleName);
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setEditModuleNameModalVisible(true);
  };

  const handleDeleteModule = (moduleName) => {
    setDeleteModuleModalVisible(true);
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setSelectedModule(moduleName);
  };

  const handleEditAssignment = (moduleName, type) => {
    setSelectedModule(moduleName);
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setEditAssignmentModalVisible(type);
  };

  const handleDeleteModuleModalClose = () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setDeleteModuleModalVisible(false);
  };

  const handleAddModuleModalClose = () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setAddModuleModalVisible(false);
  };
  const handleEditModuleNameModalClose = () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setEditModuleNameModalVisible(false);
  };

  const handleEditAssignmentModalClose = () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setEditAssignmentModalVisible(null);
  };

  const handleItemPress = (name, moduleName, type) => {
    setSelectedItem(name);
    onItemPress(name, moduleName, data.name, type);
  };

  return (
    <View
      style={[styles.container, { maxWidth: isXLarge ? 400 : isMedium ? 240 : hp(100) }]}
      aria-live="polite"
      aria-label="Content Menu"
      role="menu">
      <View style={styles.menuContainer}>
        <CustomText useTranslationText={false} text={courseName} size="xs" weight="bold" textType="display" />
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <CustomText
            role="header"
            aria-level="3"
            text="Modules"
            size="m"
            weight="ultraBold"
            textType="text"
            style={{ marginLeft: 20 }}
          />
          <CustomButton
            text="Add"
            leftIcon={<PlusCircle />}
            styleType="primaryLight"
            size="s"
            style={[styles.addButton, { marginLeft: isMedium ? "0" : "auto" }]}
            textStyle={styles.addText}
            bold={true}
            onPress={handleAddModule}
            aria-label="Add Module"
            accessibilityHint="Press to add Module"
          />
        </View>
      </View>

      <CustomModal
        visible={deleteModuleModalVisible}
        onRequestClose={handleDeleteModuleModalClose}
        showCloseButton={true}
        title="Delete Module">
        <DeleteModule
          deleteButtonClick={onDeleteModule}
          backButtonClick={handleDeleteModuleModalClose}
          moduleName={selectedModule}
          moduleId={selectedModuleData?.id}
        />
      </CustomModal>

      <EditModuleNameModalContainer
        visible={editModuleNameModalVisible}
        onClose={handleEditModuleNameModalClose}
        moduleName={selectedModule}
        onUpdateModuleName={onRenameModule}
        moduleId={selectedModuleData?.id}
      />

      <EditAddAssignmentModalContainer
        visible={!!editAssignmentModalVisible}
        onClose={handleEditAssignmentModalClose}
        initialContent={""}
        onSaveItem={onAddAssignment}
        type={editAssignmentModalVisible}
        moduleName={selectedModule}
        activelyEditingState={activelyEditingState}
      />

      <AddModuleModalContainer
        visible={addModuleModalVisible}
        onClose={handleAddModuleModalClose}
        onAddModule={onAddModule}
      />

      {loadingData ? (
        <View testID="loading-skeleton">
          <Skeleton height={34} width="98%" style={{ marginVertical: 10 }} />
        </View>
      ) : (
        <ScrollView
          role="list"
          testID="module-scroll"
          data-testid="module-scroll"
          showsVerticalScrollIndicator={true}
          style={styles.scrollView}>
          {data?.modules?.map((module, index) => {
            const moduleName = module.module_name;

            return (
              <ListItem.Accordion
                testID={`module-item-${moduleName}`}
                key={moduleName}
                role="button"
                aria-label={moduleName}
                pad={8}
                containerStyle={styles.chapterAccordion}
                isExpanded={expandedModule === moduleName}
                content={
                  <ListItem.Content
                    style={[
                      styles.chapterNameContainer,
                      { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
                    ]}>
                    <View style={{ flex: 3, paddingRight: 10 }}>
                      <CustomText
                        useTranslationText={false}
                        text={moduleName}
                        size="m"
                        weight="medium"
                        textType="text"
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        style={{ textDecorationLine: "none", color: "#000" }}
                      />
                    </View>
                    <View
                      style={{
                        flexShrink: 0,
                        flexDirection: "row",
                        gap: 8,
                        alignItems: "center",
                      }}>
                      <Pressable
                        onPress={() => {
                          handleEditModuleName(moduleName);
                        }}>
                        <EditIcon width={20} height={20} color={"#667085"} />
                      </Pressable>

                      <Pressable
                        onPress={() => {
                          handleDeleteModule(moduleName);
                        }}>
                        <Trash width={20} height={20} color={"#667085"} />
                      </Pressable>
                    </View>
                  </ListItem.Content>
                }
                onPress={() => setExpandedModule((prev) => (prev === moduleName ? null : moduleName))}
                topDivider={index !== 0}>
                <ContentMenuHeader
                  type="Assignment"
                  onAddPress={() => handleEditAssignment(moduleName, "Assignment")}
                />
                {module.assignments.map((assignment) => {
                  const assignmentName = assignment.assignment_name;
                  return (
                    <ContentMenuItem
                      onItemPress={() => handleItemPress(assignmentName, module.module_name, "Assignment")}
                      name={assignmentName}
                      key={assignmentName}
                      isSelected={selectedItem === assignmentName}
                    />
                  );
                })}
              </ListItem.Accordion>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    height: 1,
    paddingHorizontal: 20,
  },
  container: {
    top: 0,
    left: 0,
    bottom: 0,
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: "#EAECF0",
    display: "flex",
    flexDirection: "column",
    gap: 20,
    backgroundColor: "#fff",
    zIndex: 20,
    shadowColor: "#000",
    shadowOffset: {
      width: 8,
      height: 0,
    },
    shadowOpacity: 0.02,
    shadowRadius: 8,
    elevation: 5,
  },
  menuContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    paddingHorizontal: 20,
    paddingTop: 40,
  },
  chapterAccordion: {
    flexDirection: "row-reverse",
    paddingRight: 16,
    paddingLeft: 0,
    paddingVertical: 12,
  },
  chapterNameContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: 34,
  },
  addButton: {
    fontSize: 12,
  },
  addText: {
    paddingLeft: 10,
  },
});

export default ContentMenu;
